import React, { useEffect, useState } from 'react';
import {
  Image as ImageIcon, AlignLeft, Tag, DollarSign, Save, Leaf, Flame, Clock, Timer, Utensils, ChefHat, Soup, Carrot, Layers, AlertTriangle, Info,
} from 'lucide-react';
import { useAppSettings } from '../context/AppSettingsContext';
import { useToast } from '../components/ui/use-toast';

/** Menu View Settings: which dish fields the waiter sees on the order menu, backed by the AppSettings singleton. */
const FIELDS = [
  { key: 'image', label: 'Dish image', icon: ImageIcon },
  { key: 'description', label: 'Description', icon: AlignLeft },
  { key: 'category', label: 'Category label', icon: Tag },
  { key: 'price', label: 'Price on variant buttons', icon: DollarSign },
  { key: 'kind', label: 'Kind badge', icon: Layers },
  { key: 'veg', label: 'Veg / Vegan badge', icon: Leaf },
  { key: 'spicy_level', label: 'Spicy level', icon: Flame },
  { key: 'calories', label: 'Calories', icon: Flame },
  { key: 'total_time', label: 'Total time (mins)', icon: Timer },
  { key: 'prep_time', label: 'Prep time (mins)', icon: Clock },
  { key: 'cook_time', label: 'Cook time (mins)', icon: Timer },
  { key: 'serving_vessel', label: 'Serving vessel', icon: Utensils },
  { key: 'cooking_station', label: 'Cooking station', icon: ChefHat },
  { key: 'plating_note', label: 'Plating note', icon: Soup },
  { key: 'tags', label: 'Tags', icon: Tag },
  { key: 'allergens', label: 'Allergens', icon: AlertTriangle },
  { key: 'ingredients', label: 'Ingredients (ℹ button)', icon: Carrot },
  { key: 'custom_fields', label: 'Custom fields', icon: Info },
];

const DEFAULTS = {
  image: true, description: true, category: true, price: true,
  kind: false, veg: false, spicy_level: false, calories: false, total_time: false,
  prep_time: false, cook_time: false,
  serving_vessel: false, cooking_station: false, plating_note: false,
  tags: false, allergens: false, ingredients: false, custom_fields: false,
};

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-emerald-500' : 'bg-red-500'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function MenuViewSettings() {
  const { settings, loaded, update } = useAppSettings();
  const { toast } = useToast();
  const [fields, setFields] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  // Sync local state once settings load / change.
  useEffect(() => {
    if (settings?.order_menu_fields) {
      setFields({ ...DEFAULTS, ...settings.order_menu_fields });
    }
  }, [settings]);

  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await update({ order_menu_fields: fields });
      toast({ title: 'Saved', description: 'Menu view settings updated.' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || 'Could not save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
          Menu View Settings
        </h1>
      </header>

      <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {FIELDS.map(({ key, label, icon: Icon }) => (
          <div key={key} className="flex items-center justify-between gap-4 px-4 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <Icon className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="text-sm font-medium text-slate-900 min-w-0">{label}</div>
            </div>
            <Toggle checked={!!fields[key]} onChange={(v) => setField(key, v)} />
          </div>
        ))}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || !loaded}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          <Save size={16} />
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
